import React, { useEffect, useState } from 'react';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { DataService } from '../../services/data-service';
import Loader from 'src/components/Loader';
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router';
import { Box, Paper, Typography, Stack, alpha, useTheme } from '@mui/material';
import EmployeeDrawer from './EmployeeDrawer';
import PermissionService, { ACTION, ROLE } from 'src/services/permission-service';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

export interface Employee {
  Id: string;
  Name: string;
  Email: string;
  PhoneNumber: string;
  Role: string;
}

const EmployeesListing: React.FC = () => {
  const navigation = useNavigate();
  const [employees, setEmployees] = useState<Record<string, Employee>>({});
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [updateData, setUpdateData] = useState<Employee | undefined>(undefined);
  const userRole = useAppSelector(s => s.user.user.role) as ROLE;
  const dispatch = useAppDispatch()

  // Table actions
  const actions: ITableMenuAction[] = [
    {
      key: 'add',
      title: 'Add Employee',
      icon: <AddIcon />,
      disabled: (selected: string[]) => false,
      onClick: () => {
        setUpdateData(undefined);
        setOpenDrawer(true);
      },
      visible: () => PermissionService.getInstance().isAllowed(ACTION.CREATE_EMPLOYEE, userRole),
    }
  ];

  // Fetch employees
  const initialize = async () => {
    setLoading(true);
    try {
      const res = await DataService.get('api/Employee/all');
      const response = await res.json();
      const data: Employee[] = response.data || [];
      const result: Record<string, Employee> = {};
      data.forEach((e) => {
        result[e.Id!] = {
          ...e
        };
      });
      setEmployees(result);
    } catch (e) {
      console.error('Failed to fetch employees:', e);
      toast.error('Failed to load employees');
    }
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, []);

  // Table headers
  const headers: EnhancedTableColumn[] = [
    {
      id: 'Name',
      label: 'Name',
      type: EnhancedTableColumnType.TEXT,
      // onClick: (id: string) => {
      //   navigation(`/employee/${id}`);
      // },
    },
    {
      id: 'Email',
      label: 'Email',
      type: EnhancedTableColumnType.TEXT
    },
    {
      id: 'PhoneNumber',
      label: 'Phone',
      type: EnhancedTableColumnType.TEXT
    },
    {
      id: 'Role',
      label: 'Role',
      type: EnhancedTableColumnType.TEXT
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.ACTION,
      actions: [
        {
          onClick: (id: string) => {
            setUpdateData(employees[id]);
            setOpenDrawer(true);
          },
          icon: <EditIcon fontSize="small" />,
          label: 'Edit',
          visible: () => PermissionService.getInstance().isAllowed(ACTION.EDIT_EMPLOYEE, userRole),
        },
        {
          onClick: async (id: string) => {
            const req = await DataService.patch(`api/Employee/make-admin?id=${id}`, {});
            let responseJson = null;
            try {
              responseJson = await req.json();
            } catch (e) {}
            if (req.ok && responseJson && responseJson.success) {
              dispatch(showAlert({ message: 'User changed to admin', type: 'success' }));
              initialize();
            } else {
              dispatch(showAlert({ message: responseJson.message || 'Failed to update employee', type: 'error' }));
            }
          },
          icon: <AdminPanelSettingsIcon fontSize="small" color="primary" />,
          label: 'Make Admin',
          visible: (row: any) => row.Role !== 'Admin',
        }
      ]
    }
  ];

  // Prepare data for EnhancedTable
  const dataObj: Record<string, any> = Object.fromEntries(
    Object.entries(employees).map(([id, emp]) => [
      id,
      {
        ...emp,
      }
    ])
  );

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
      >
        <Loader/>
      </Box>
    );
  }

  return (
    <Box>
      {/* Stats Cards (Optional) */}
      <Stack direction="row" spacing={3} sx={{ mb: 3 }}>
        <Paper
          sx={{
            p: 3,
            flex: 1,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}
        >
          <Typography variant="h3" fontWeight={700} color="primary.main">
            {Object.keys(employees).length}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Total Employees
          </Typography>
        </Paper>
        
        <Paper
          sx={{
            p: 3,
            flex: 1,
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
          }}
        >
          <Typography variant="h3" fontWeight={700} color="success.main">
            {Object.values(employees).filter(emp => emp.Role === 'Admin').length}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Admin Users
          </Typography>
        </Paper>
      </Stack>

      {/* Main Content */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <EnhancedTable
          title="All Users"
          columns={headers}
          data={dataObj}
          actions={actions}
          collapsible={true}
          striped={true}
          boxedCells={true}
          wmsMode={true}
        />
      </Paper>

      {/* Employee Drawer */}
      {openDrawer && (
        <EmployeeDrawer
          open={openDrawer}
          onClose={() => { 
            setOpenDrawer(false); 
            setUpdateData(undefined); 
          }}
          onSuccess={() => { 
            setOpenDrawer(false); 
            setUpdateData(undefined); 
            initialize(); 
          }}
          isAdmin={true}
          updateData={updateData}
        />
      )}
    </Box>
  );
};

export default EmployeesListing;